import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getSocket } from "../../utils/socket";
import { FaTrash, FaFlag, FaExclamationTriangle } from "react-icons/fa";

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchSender, setSearchSender] = useState("");
  const [searchReceiver, setSearchReceiver] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [messagesPerPage] = useState(10);

  const navigate = useNavigate();
  const { user, fetchWithRetry, isLoading, logout } = useAuth();

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const queryParams = new URLSearchParams();
      if (searchSender) queryParams.append("senderId", searchSender);
      if (searchReceiver) queryParams.append("receiverId", searchReceiver);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);

      const res = await fetchWithRetry({
        method: "get",
        url: `/api/admin/messages?${queryParams.toString()}`,
      });
      setMessages(res.data);
      setFilteredMessages(res.data);
      setLoading(false);
    } catch (error) {
      console.error("Lỗi tải danh sách tin nhắn:", error);
      if (error.response?.status === 404) {
        setError("Không tìm thấy tin nhắn nào!");
      } else if (error.response?.status === 403 || error.response?.status === 401) {
        setError("Bạn không có quyền truy cập trang này! Vui lòng đăng nhập lại.");
        setTimeout(() => logout(), 3000);
      } else {
        setError(error.response?.data?.message || "Không thể tải danh sách tin nhắn!");
      }
      setLoading(false);
    }
  }, [fetchWithRetry, logout, searchSender, searchReceiver, startDate, endDate]);

  const handleDeleteMessage = useCallback(
    async (messageId) => {
      if (!window.confirm("Bạn có chắc muốn xóa tin nhắn này?")) return;
      try {
        await fetchWithRetry({
          method: "delete",
          url: "/api/admin/messages",
          data: { messageId },
        });
        setMessages((prevMessages) => prevMessages.filter((msg) => msg._id !== messageId));
        setFilteredMessages((prevMessages) => prevMessages.filter((msg) => msg._id !== messageId));
        setSuccessMessage("Xóa tin nhắn thành công!");
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error("Lỗi xóa tin nhắn:", error);
        setError(error.response?.data?.message || "Không thể xóa tin nhắn!");
      }
    },
    [fetchWithRetry]
  );

  const handleFlagMessage = useCallback(
    async (messageId) => {
      if (!window.confirm("Bạn có chắc muốn đánh dấu tin nhắn này là vi phạm?")) return;
      try {
        const response = await fetchWithRetry({
          method: "post",
          url: "/api/admin/messages/flag",
          data: { messageId },
        });
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg._id === messageId ? { ...msg, isFlagged: true } : msg
          )
        );
        setFilteredMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg._id === messageId ? { ...msg, isFlagged: true } : msg
          )
        );
        setSuccessMessage(response.data.message);
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error("Lỗi đánh dấu tin nhắn:", error);
        setError(error.response?.data?.message || "Không thể đánh dấu tin nhắn!");
      }
    },
    [fetchWithRetry]
  );

  const handleSendWarning = useCallback(
    async (messageId, userId) => {
      if (!window.confirm("Bạn có chắc muốn gửi cảnh báo cho người dùng này?")) return;
      try {
        const response = await fetchWithRetry({
          method: "post",
          url: "/api/admin/messages/warn",
          data: {
            messageId,
            userId,
            warningMessage: "Bạn đã vi phạm quy định với tin nhắn này. Vui lòng tuân thủ quy tắc cộng đồng!",
          },
        });
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg._id === messageId ? { ...msg, warningSent: true } : msg
          )
        );
        setFilteredMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg._id === messageId ? { ...msg, warningSent: true } : msg
          )
        );

        const socket = getSocket();
        if (socket.connected) {
          socket.emit("notification", {
            userId,
            type: "warning",
            message: "Bạn đã vi phạm quy định với tin nhắn này. Vui lòng tuân thủ quy tắc cộng đồng!",
          });
        }

        setSuccessMessage(response.data.message);
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error("Lỗi gửi cảnh báo:", error);
        setError(error.response?.data?.message || "Không thể gửi cảnh báo!");
      }
    },
    [fetchWithRetry]
  );

  useEffect(() => {
    let filtered = [...messages];
    setFilteredMessages(filtered);
    setCurrentPage(1);
  }, [messages]);

  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = filteredMessages.slice(indexOfFirstMessage, indexOfLastMessage);
  const totalPages = Math.ceil(filteredMessages.length / messagesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    if (isLoading) return;

    if (!user || !user.permissions.includes("view_private_messages")) {
      setError("Bạn không có quyền truy cập trang này!");
      navigate("/login");
      return;
    }
    loadMessages();
  }, [user, navigate, loadMessages, isLoading]);

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Quản lý tin nhắn (Admin)</h2>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}
      {successMessage && (
        <p className={`mb-4 ${successMessage.includes("thành công") ? "text-green-500" : "text-red-500"}`}>
          {successMessage}
        </p>
      )}

      <div className="flex flex-wrap gap-4 mb-6 items-center">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Tìm kiếm người gửi (ID)..."
            value={searchSender}
            onChange={(e) => setSearchSender(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Tìm kiếm người nhận (ID)..."
            value={searchReceiver}
            onChange={(e) => setSearchReceiver(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="min-w-[150px]">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Từ ngày"
          />
        </div>
        <div className="min-w-[150px]">
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Đến ngày"
          />
        </div>
        <button
          onClick={loadMessages}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
        >
          Tìm kiếm
        </button>
      </div>

      {loading ? (
        <p className="text-center text-gray-500">Đang tải...</p>
      ) : currentMessages.length === 0 ? (
        <p className="text-center text-gray-500">Không có tin nhắn nào để hiển thị.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-lg shadow-lg">
            <thead>
              <tr className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <th className="p-4 text-left font-semibold">Người gửi</th>
                <th className="p-4 text-left font-semibold">Người nhận</th>
                <th className="p-4 text-left font-semibold">Nội dung</th>
                <th className="p-4 text-left font-semibold">Thời gian</th>
                <th className="p-4 text-left font-semibold">Trạng thái</th>
                <th className="p-4 text-left font-semibold">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {currentMessages.map((message) => (
                <tr key={message._id} className="border-b hover:bg-gray-50 transition duration-200">
                  <td className="p-4 text-gray-800">{message.senderId?.name || "N/A"}</td>
                  <td className="p-4 text-gray-800">{message.receiverId?.name || "N/A"}</td>
                  <td className="p-4 text-gray-600">{message.content}</td>
                  <td className="p-4 text-gray-600">{new Date(message.createdAt).toLocaleString("vi-VN")}</td>
                  <td className="p-4 text-gray-600">
                    {message.isFlagged ? (
                      <span className="text-red-500">Vi phạm</span>
                    ) : (
                      <span className="text-green-500">Bình thường</span>
                    )}
                    {message.warningSent && (
                      <span className="ml-2 text-yellow-500">(Đã gửi cảnh báo)</span>
                    )}
                  </td>
                  <td className="p-4 space-x-2">
                    {!message.isFlagged && (
                      <button
                        onClick={() => handleFlagMessage(message._id)}
                        className="px-3 py-1 rounded bg-orange-500 text-white hover:bg-orange-600 transition duration-200"
                        title="Đánh dấu vi phạm"
                      >
                        <FaFlag />
                      </button>
                    )}
                    {message.isFlagged && !message.warningSent && (
                      <button
                        onClick={() => handleSendWarning(message._id, message.senderId._id)}
                        className="px-3 py-1 rounded bg-yellow-500 text-white hover:bg-yellow-600 transition duration-200"
                        title="Gửi cảnh báo"
                      >
                        <FaExclamationTriangle />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteMessage(message._id)}
                      className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition duration-200"
                      title="Xóa tin nhắn"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <nav className="inline-flex rounded-md shadow">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              Trước
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`px-3 py-1 border border-gray-300 ${
                  currentPage === page
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-500 hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              Sau
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;